import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, FileText, ArrowRight } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../auth";
import UnifiedComposer from "../components/UnifiedComposer";
import { QuickCapture } from "../components/QuickCapture";
import { Card, projectDisplayName } from "../ui";
import { katalog, useT } from "../i18n";
import { USE_CASES, HOME_CHIP_IDS, type UseCase } from "../useCases";
import { istStudioZiel } from "../studioAccess";

interface Overview {
  total_open_tasks: number;
  total_due_this_week: number;
}

interface ChatRow {
  id: string;
  project_name: string | null;
  title: string;
  updated_at: string;
}

interface DocRow {
  id: string;
  title: string;
  created_at: string;
}

/** Vereinheitlichter „Zuletzt"-Eintrag (Chat oder Dokument). */
interface RecentItem {
  key: string;
  kind: "chat" | "doc";
  title: string;
  project: string;
  time: string;
  to: string;
}

// Keine Komponente → `katalog()` statt `useT()` (docs/i18n.md §3). Der letzte
// Zweig bleibt bewusst `de-DE`: das ist ein DATUM, und Datumsformate bleiben in
// jeder Oberflächensprache deutsch.
function fmtRelative(iso: string) {
  const t = katalog().overview;
  const d = new Date(iso);
  const diffSec = (Date.now() - d.getTime()) / 1000;
  if (diffSec < 60) return t.gerade;
  if (diffSec < 3600) return t.vorMinuten(Math.floor(diffSec / 60));
  if (diffSec < 86400) return t.vorStunden(Math.floor(diffSec / 3600));
  if (diffSec < 86400 * 7) return t.vorTagen(Math.floor(diffSec / 86400));
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "short" });
}

/** Vorschlags-Chip: Icon + Kurz-Label, Beschreibung nur als Tooltip. */
function UseCaseChip({
  uc,
  onAction,
}: {
  uc: UseCase;
  onAction: () => void;
}) {
  const t = useT();
  const Icon = uc.icon;
  const label = uc.chip ?? uc.title;
  const base =
    "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-sm text-text-2 transition-colors";
  const content = (
    <>
      <Icon className="w-3.5 h-3.5 text-text-3" />
      <span>{label}</span>
      {uc.soon && <span className="text-text-3">{t.overview.bald}</span>}
    </>
  );

  // `data-home-chip` macht die Chip-Zeile zählbar — ohne Marker müsste ein Walk
  // über Beschriftungstexte gehen, und eine umbenannte Beschriftung hätte die
  // Zusage still ausgehebelt.
  if (uc.soon) {
    return (
      <span
        data-home-chip={uc.id}
        className={base + " opacity-50 cursor-default"}
        title={uc.description}
      >
        {content}
      </span>
    );
  }
  if (uc.action) {
    return (
      <button
        type="button"
        data-home-chip={uc.id}
        onClick={onAction}
        className={base + " hover:border-accent-300 hover:text-text"}
        title={uc.description}
      >
        {content}
      </button>
    );
  }
  return (
    <Link
      to={uc.to!}
      data-home-chip={uc.id}
      className={base + " hover:border-accent-300 hover:text-text"}
      title={uc.description}
    >
      {content}
    </Link>
  );
}

export default function OverviewPage() {
  const { username } = useAuth();
  const t = useT();
  const [overview, setOverview] = useState<Overview | null>(null);
  const [recentChats, setRecentChats] = useState<ChatRow[]>([]);
  const [recentDocs, setRecentDocs] = useState<DocRow[]>([]);
  const [quickOpen, setQuickOpen] = useState(false);

  // Mobil (<md) wird der Composer ans Viewport-Ende gepinnt (bottom-zentriert),
  // Desktop bleibt unverändert inline. Breakpoint reaktiv, damit ein Resize
  // sauber umschaltet. matchMedia = keine neue Dependency.
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 767px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setIsMobile(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  // Tastatur-Handling (mobil): der fixe Composer soll ÜBER der eingeblendeten
  // Bildschirmtastatur bleiben. Einfacher Ansatz — bottom an visualViewport
  // koppeln: kbInset = wie viel die Tastatur vom Layout-Viewport verdeckt.
  // Einschränkung: rein visualViewport-basiert; auf Browsern ohne die API
  // (sehr alt) fällt es auf bottom:0 zurück. Dokumentiert & bewusst.
  const [kbInset, setKbInset] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKbInset(inset);
    };
    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onResize);
    onResize();
    return () => {
      vv.removeEventListener("resize", onResize);
      vv.removeEventListener("scroll", onResize);
    };
  }, []);

  useEffect(() => {
    api.get<Overview>("/views/overview").then(setOverview).catch(() => {});
    api.get<ChatRow[]>("/chats?limit=20").then(setRecentChats).catch(() => {});
    api.get<DocRow[]>("/documents?limit=20").then(setRecentDocs).catch(() => {});
  }, []);

  const hour = new Date().getHours();
  const greet =
    hour < 11 ? t.overview.gutenMorgen : hour < 18 ? t.overview.hallo : t.overview.gutenAbend;

  // Home-Chips in fixer Reihenfolge (HOME_CHIP_IDS). KEIN Sichtbarkeits-Filter:
  // das Band ist für jede Anmeldung dasselbe und ändert sich nach dem Laden
  // nicht mehr — ein Band, dessen Inhalt an der Server-Antwort hängt, verschiebt
  // Klickziele (Messwerte in useCases.ts).
  //
  // Der `istStudioZiel`-Filter ist deshalb KEINE Sichtbarkeitsregel, sondern
  // eine Invariante: hier steht nie ein Studio-Ziel, egal was künftig in
  // HOME_CHIP_IDS landet. Genau an dieser Stelle ist die Regel beim ersten
  // Anlauf durchgerutscht (die Leiste war gegatet, das Band nicht) — die Liste
  // und der Filter müssten jetzt BEIDE falsch sein, damit das nochmal passiert.
  const chips = useMemo(
    () =>
      HOME_CHIP_IDS.map((id) => USE_CASES.find((u) => u.id === id))
        .filter((u): u is UseCase => Boolean(u))
        .filter((u) => !istStudioZiel(u.to)),
    []
  );

  // „Zuletzt": Chats + Dokumente nach Zeit gemischt, dedupliziert
  // (Titel+Projekt), max. 5 Einträge.
  const recent = useMemo<RecentItem[]>(() => {
    const items: (RecentItem & { ts: number })[] = [
      ...recentChats.map((c) => ({
        key: `chat-${c.id}`,
        kind: "chat" as const,
        title: c.title,
        project: projectDisplayName(c.project_name),
        time: c.updated_at,
        to: `/chat/${c.id}`,
        ts: +new Date(c.updated_at),
      })),
      ...recentDocs.map((d) => ({
        key: `doc-${d.id}`,
        kind: "doc" as const,
        title: d.title,
        project: "",
        time: d.created_at,
        to: `/docs/${d.id}`,
        ts: +new Date(d.created_at),
      })),
    ];
    items.sort((a, b) => b.ts - a.ts);
    const seen = new Set<string>();
    const out: RecentItem[] = [];
    for (const it of items) {
      const dedupe = `${it.title}·${it.project}`;
      if (seen.has(dedupe)) continue;
      seen.add(dedupe);
      out.push(it);
      if (out.length >= 5) break;
    }
    return out;
  }, [recentChats, recentDocs]);

  const dueCount = overview?.total_due_this_week ?? 0;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 md:py-16 space-y-8 pb-44 md:pb-16">
      <header className="text-center">
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-text tracking-tight">
          {greet}
          {username ? (
            <>
              , <span className="text-accent-600">{username}</span>
            </>
          ) : (
            ""
          )}
        </h1>
      </header>

      {/* Primärelement: EIN Eingabefeld. Desktop inline (nach der Begrüßung);
          mobil ans Viewport-Ende gepinnt (bottom-zentriert, Safe-Area + über der
          Tastatur). Der übrige Content scrollt dahinter (pb-44 am Container). */}
      <div
        className={
          isMobile
            ? "fixed inset-x-0 z-20 border-t border-border bg-canvas px-4 pt-2.5"
            : ""
        }
        style={
          isMobile
            ? {
                bottom: kbInset > 0 ? `${kbInset}px` : 0,
                paddingBottom:
                  kbInset > 0
                    ? "0.625rem"
                    : "calc(env(safe-area-inset-bottom) + 0.625rem)",
              }
            : undefined
        }
      >
        <div className={isMobile ? "max-w-3xl mx-auto" : ""}>
          <UnifiedComposer />
        </div>
      </div>

      {/* Vorschlags-Chips (Icon + Kurz-Label, kein Erklärtext). */}
      <div className="flex flex-wrap justify-center gap-2">
        {chips.map((uc) => (
          <UseCaseChip key={uc.id} uc={uc} onAction={() => setQuickOpen(true)} />
        ))}
      </div>

      {/* Diese Woche fällig — genau eine Zeile, nur wenn > 0. */}
      {dueCount > 0 && (
        <Link
          to="/tasks"
          className="flex items-center justify-center gap-1.5 text-sm text-text-2 hover:text-accent-600 transition-colors"
        >
          <span>{t.overview.faelligDieseWoche(dueCount)}</span>
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      )}

      {/* Zuletzt — max 5 einzeilige Einträge, Chats + Dokumente gemischt. */}
      {recent.length > 0 && (
        <section className="space-y-2">
          <div className="flex items-end justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-text-3">
              {t.overview.zuletzt}
            </h2>
            <Link
              to="/chat"
              className="text-xs text-text-3 hover:text-accent-600 transition-colors"
            >
              {t.overview.alleChats}
            </Link>
          </div>
          <Card padding="none" className="divide-y divide-border">
            {recent.map((it) => (
              <Link
                key={it.key}
                to={it.to}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface-2 transition-colors"
              >
                {it.kind === "chat" ? (
                  <MessageSquare className="w-4 h-4 text-text-3 shrink-0" />
                ) : (
                  <FileText className="w-4 h-4 text-text-3 shrink-0" />
                )}
                <span className="text-sm text-text truncate flex-1">
                  {it.title}
                </span>
                {it.project && (
                  <span className="text-xs text-text-3 shrink-0 hidden sm:block">
                    {it.project}
                  </span>
                )}
                <span className="text-xs text-text-3 shrink-0">
                  {fmtRelative(it.time)}
                </span>
              </Link>
            ))}
          </Card>
        </section>
      )}

      <QuickCapture open={quickOpen} onClose={() => setQuickOpen(false)} />
    </div>
  );
}
